import React, { useState, useEffect } from "react";
import {
  ModalOverlay,
  ModalContent,
  Input,
  Button,
  CloseButton,
  SearchResults,
  SearchResultItem,
  ProductDetailsSection,
  ProductDetailItem,
} from "./SearchProductModalStyles";
import { fetchProducts, fetchItem } from "../../network/Services";

export enum SearchType {
  Manufacturer = "manufacturer",
  Product = "product",
}

interface Product {
  [product_ndc: string]: any;
  ndc: string;
  drugName: string;
  strength?: string;
  dosage?: string;
  package_size?: string | number;
  Controlled_Substance_Code?: string;
}

interface SearchProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: Product) => void;
  searchType: SearchType;
  selectedManufacturer?: string;
  onManufacturerSelect: (manufacturer: string) => void;
  manufacturers: { drugName: string }[];
  loadingManufacturers: boolean;
  loaderDispatch: any;
  setValue: (field: any, value: any) => void;
  onManufacturerSearchChange?: (search: string) => void;
}

const SearchProductModal: React.FC<SearchProductModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  searchType,
  selectedManufacturer,
  onManufacturerSelect,
  manufacturers,
  loadingManufacturers,
  loaderDispatch,
  setValue,
  onManufacturerSearchChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [productDetails, setProductDetails] = useState<Product | null>(null);
  const [currentSearchType, setCurrentSearchType] = useState(searchType);

  const fetchData = async (
  currentSearchType: SearchType,
  searchTerm: string,
  selectedManufacturer: string | undefined,
  manufacturers: { drugName: string }[],
  setResults: React.Dispatch<React.SetStateAction<Product[]>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  fetchProducts: Function
) => {
  try {
    setLoading(true);

    if (currentSearchType === SearchType.Manufacturer) {
      const filteredManufacturers =
        searchTerm === ""
          ? manufacturers
          : manufacturers.filter((m) =>
              m.drugName.toLowerCase().startsWith(searchTerm.toLowerCase())
            );

      const formatted = filteredManufacturers.map((m) => ({
        ndc: "",
        drugName: m.drugName,
      }));

      setResults(formatted);
    } else {
      if (!(window as any).productsPage || (searchTerm || "").trim() !== (window as any).productsQuery) {
        (window as any).productsPage = 0;
        (window as any).productsQuery = (searchTerm || "").trim();
        (window as any).productsHasMore = true;
      }
      const page = (window as any).productsPage as number;
      const data = await fetchProducts(selectedManufacturer ?? "", (searchTerm || '').trim(), page, 50);
      if (page === 0) setResults(data);
      else setResults(prev => [...prev, ...data]);
      (window as any).productsHasMore = Array.isArray(data) && data.length === 50;
    }
  } catch (error) {
    console.error("Error fetching search results:", error);
  } finally {
    setLoading(false);
  }
};

    useEffect(() => {
      const timeoutId = setTimeout(() => {
      if (currentSearchType === SearchType.Manufacturer && onManufacturerSearchChange) {
        onManufacturerSearchChange((searchTerm || '').trim());
      }
        fetchData(
          currentSearchType,
          searchTerm,
          selectedManufacturer,
          manufacturers,
          setResults,
          setLoading,
          fetchProducts
        );
      }, 300);

      return () => clearTimeout(timeoutId);
    }, [searchTerm, currentSearchType, selectedManufacturer, manufacturers]);


  const handleManufacturerSelect = (manufacturer: string) => {
    onManufacturerSelect(manufacturer);
    setSearchTerm("");
    setResults([]);
    setCurrentSearchType(SearchType.Product);
  };

  const handleSelect = async (item: Product) => {
    setFetchingDetails(true);
    try {
      const details = await fetchItem(item.ndc, loaderDispatch);
      setProductDetails(details);
    } catch (error) {
      console.error("Error fetching product details:", error);
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleConfirmSelection = () => {
    if (productDetails) {
      onSelect(productDetails);
      setValue("ndc", productDetails.product_ndc);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <h2>
          Search{" "}
          {currentSearchType === SearchType.Manufacturer
            ? "Manufacturer"
            : "Product"}
        </h2>
        <Input
          type="text"
          placeholder={`Search ${currentSearchType}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {loading || loadingManufacturers ? <p>Loading...</p> : null}
        <SearchResults
          onScroll={(e) => {
            const el = e.currentTarget as HTMLUListElement;
            const nearBottom =
              el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
            if (
              nearBottom &&
              currentSearchType === SearchType.Manufacturer &&
              (window as any).manufacturersFetchNextPage
            ) {
              (window as any).manufacturersFetchNextPage();
            }
            if (
              nearBottom &&
              currentSearchType === SearchType.Product &&
              (window as any).productsHasMore
            ) {
              (window as any).productsPage =
                ((window as any).productsPage || 0) + 1;
              fetchData(
                currentSearchType,
                searchTerm,
                selectedManufacturer,
                manufacturers,
                setResults,
                setLoading,
                fetchProducts,
              );
            }
          }}
        >
          {!(loading || loadingManufacturers) && results.length === 0 && (
            <p>No Data Found</p>
          )}
          {results.map((item, index) => (
            <SearchResultItem
              key={item.ndc || index}
              onClick={() =>
                currentSearchType === SearchType.Manufacturer
                  ? handleManufacturerSelect(item.drugName)
                  : handleSelect(item)
              }
            >
              {item.drugName}
            </SearchResultItem>
          ))}
        </SearchResults>

        {fetchingDetails && <p>Fetching product details...</p>}
        {productDetails && (
          <ProductDetailsSection>
            <h3>Product Details</h3>
            {productDetails.product_ndc && (
              <ProductDetailItem>
                <strong>NDC:</strong> {productDetails.product_ndc}
              </ProductDetailItem>
            )}
            {productDetails.strength && (
              <ProductDetailItem>
                <strong>Strength:</strong> {productDetails.strength}
              </ProductDetailItem>
            )}
            {productDetails.dosage && (
              <ProductDetailItem>
                <strong>Dosage:</strong> {productDetails.dosage}
              </ProductDetailItem>
            )}
            {productDetails.package_size && (
              <ProductDetailItem>
                <strong>Package Size:</strong> {productDetails.package_size}
              </ProductDetailItem>
            )}
            {productDetails.Controlled_Substance_Code && (
              <ProductDetailItem>
                <strong>Controlled Substance Code:</strong>{" "}
                {productDetails.Controlled_Substance_Code}
              </ProductDetailItem>
            )}
            <Button onClick={handleConfirmSelection}>Confirm</Button>
          </ProductDetailsSection>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default SearchProductModal;
